import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import DefaultImg from "../../../assets/img/default-image.svg";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getCandidatePendingTimesheetThunk, submitCandidateTimeSheetThunk } from "../../../store/timesheet/thunks";
import {
  ICandidateLogTimesheet,
  ISubmitTimesheetParams,
} from "../../../store/timesheet/types";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import SaveNotification from "../../SaveNotification";
import CustomButton from "../../UI/Buttons/CustomButton";
import Break from "../../UI/Icons/Break";
import LogIn from "../../UI/Icons/LogIn";
import LogOut from "../../UI/Icons/LogOut";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import TimePicker12Hr from "../../UI/Inputs/TimePicker";
import PopupModal from "../../UI/Popup";
import ToggleButton, { ToggleOption } from "../../UI/ToggleButton";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

interface LogtimesheetProps {
  /**
   * List of candidate pending timesheets
   */
  candidatePendingTimesheetList?: ICandidateLogTimesheet[] | null;
}

const Logtimesheet: React.FC<LogtimesheetProps> = ({
  candidatePendingTimesheetList,
}) => {
  const dispatch = useAppDispatch();

  const inProgress = useAppSelector((state) => state.timesheet.inProgress);
  const user = useAppSelector((state) => state.auth.user);
  const [isNotificationActive, setIsNotificationActive] = useState(false);
  const [showLogModal, setShowLogModal] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState(false);
  const [currentTimesheetIndex, setCurrentTimesheetIndex] = useState(0);
  const [currentTimesheet, setCurrentTimesheet] =
    useState<ICandidateLogTimesheet | null>(null);
  const [actualJobStartTime, setActualJobStartTime] = useState<string>("");
  const [actualJobEndTime, setActualJobEndTime] = useState<string>("");
  const [selectedWorkType, setSelectedWorkType] = useState('worked');
  const [iDidNotWorkOnThisDay, setIDidNotWorkOnThisDay] = useState<boolean>(false);
  const [savedTimeValues, setSavedTimeValues] = useState({
    job_start_time: "",
    job_end_time: "",
    break_start_time: "",
    break_end_time: "",
  });
  const [initialFormValues, setInitialFormValues] = useState<ISubmitTimesheetParams>({
    job_start_time: "",
    job_end_time: "",
    break_start_time: "",
    break_end_time: "",
    candidate_id: "",
    vacancy_id: "",
    timeframe_id: 0
  });
  const totalTimeSheetCount =
    Object.keys(candidatePendingTimesheetList ?? []).filter(item => item !== "token").length || 0;

    const options: ToggleOption[] = [
      { label: 'I did work', value: 'worked' },
      { label: 'I did not work on this day', value: 'not-worked' }
    ];



  const handleTimeChange = (value: string | null, fieldName: string) => {
    if (value) {
      // setTime12hr(value);
      setFieldValue(fieldName, value); // Convert to 24-hour format
      
      // Clear error for this field when user enters a value
      if (errors[fieldName as keyof typeof errors]) {
        setFieldError(fieldName, "");
      }
    }
  };

  // Show modal if there are timesheets to log
  useEffect(() => {
    if (totalTimeSheetCount >= 1) {
      setShowLogModal(true);
    } else {
      setShowLogModal(false);
    }
  }, [totalTimeSheetCount]);

  // Update current timesheet when index or list changes
  useEffect(() => {
    setCurrentTimesheet(
      candidatePendingTimesheetList?.[currentTimesheetIndex] ?? null
    );
    // Reset saved time values when switching to a new timesheet
    setSavedTimeValues({
      job_start_time: "",
      job_end_time: "",
      break_start_time: "",
      break_end_time: "",
    });
  }, [currentTimesheetIndex, candidatePendingTimesheetList]);

  // Sync actual job times with current timesheet
  useEffect(() => {
    setActualJobStartTime(
      currentTimesheet?.start_time
        ? currentTimesheet.start_time.slice(0, 5)
        : ""
    );
    setActualJobEndTime(
      currentTimesheet?.end_time ? currentTimesheet.end_time.slice(0, 5) : ""
    );
  }, [currentTimesheet]);

  const handleGetCandidateTimesheet = async () => {
    await dispatch(getCandidatePendingTimesheetThunk())
  }

  const {
    handleSubmit,
    values,
    errors,
    setFieldValue,
    resetForm,
    setFieldError,
    submitCount
  } = useFormik<ISubmitTimesheetParams>({
    validationSchema: iDidNotWorkOnThisDay ? undefined : createValidationSchema(
      "candidate_log_timesheet",
      actualJobStartTime,
      actualJobEndTime,
      "",
      "",
      iDidNotWorkOnThisDay
    ),
    initialValues: {
      job_start_time: "",
      job_end_time: "",
      break_start_time: "",
      break_end_time: "",
      candidate_id: user?.uuid || "",
      vacancy_id: currentTimesheet?.vacancy_uuid || "",
      timeframe_id: currentTimesheet?.timeframe_id || 0
    },
    validateOnBlur: false,  // Prevents validation on blur
    validateOnChange: true, // Enable validation on change to clear errors when valid
    onSubmit: async (values: ISubmitTimesheetParams) => {
      if (!currentTimesheet) return; // No timesheet to log
      const payload: ISubmitTimesheetParams = {
        job_start_time: currentTimesheet.date + " " + values.job_start_time,
        job_end_time: currentTimesheet.date + " " + values.job_end_time,
        break_start_time:
          values.break_start_time &&
          currentTimesheet.date + " " + values.break_start_time,
        break_end_time:
          values.break_end_time &&
          currentTimesheet.date + " " + values.break_end_time,
        vacancy_id: currentTimesheet?.vacancy_uuid || "",
        candidate_id: user?.uuid || "",
        timeframe_id: currentTimesheet.timeframe_id || 0,
        did_not_work: iDidNotWorkOnThisDay
      };
      const response = await dispatch(submitCandidateTimeSheetThunk(payload));
      if (isFulfilled(response)) {
        setIsNotificationActive(true); // Set this before showing notification
        setShowLogModal(false); // Hide modal temporarily
        setShowNotification(true); // Show Submitted popup

        setTimeout(() => {
          setShowNotification(false); // Hide notification after 3s
          setIsNotificationActive(false); // Reset

          if (currentTimesheetIndex < totalTimeSheetCount - 1) {
            setCurrentTimesheetIndex(currentTimesheetIndex + 1);
            resetForm();
            setIDidNotWorkOnThisDay(false); // Reset checkbox for next timesheet
            setSelectedWorkType('worked'); // Reset toggle for next timesheet
            setShowLogModal(true); // Show modal again for next timesheet
          } else if(currentTimesheetIndex === totalTimeSheetCount - 1) {
            handleGetCandidateTimesheet()
          }
          // No need to show the modal if all done
        }, 3000);
      } else {
        const payload = response.payload as {
          errors?: Record<string, string | string[]>;
        };
        const errorResponse = payload && payload.errors;
        if (errorResponse) {
          Object.entries(errorResponse).forEach(([field, messages]) => {
            // If the API returns an array of messages, join them into a single string
            setFieldError(
              field,
              Array.isArray(messages) ? messages.join(" ") : messages
            );
          });
        }
      }
    },
  });

  // Check if form values have changed from initial values
  const hasValuesChanged = () => {
    // If 'I did not work on this day' is checked, allow submit
    if (iDidNotWorkOnThisDay) {
      return true;
    }
    const currentJobStartTime = values.job_start_time || "";
    const currentJobEndTime = values.job_end_time || "";
    const currentBreakStartTime = values.break_start_time || "";
    const currentBreakEndTime = values.break_end_time || "";
    
    const initialJobStartTime = initialFormValues.job_start_time || "";
    const initialJobEndTime = initialFormValues.job_end_time || "";
    const initialBreakStartTime = initialFormValues.break_start_time || "";
    const initialBreakEndTime = initialFormValues.break_end_time || "";
    
    const hasChanged = (
      currentJobStartTime !== initialJobStartTime ||
      currentJobEndTime !== initialJobEndTime ||
      currentBreakStartTime !== initialBreakStartTime ||
      currentBreakEndTime !== initialBreakEndTime
    );

    if(currentTimesheet?.is_declined_timesheet) {
      return hasChanged;
    } else {
      // For new timesheets, only job start and end times are relevant
      return true;
    }
    
  };

  // Pre-fill form values for declined timesheets
  useEffect(() => {
    if (currentTimesheet) {
      let jobStartTime = "";
      let jobEndTime = "";
      let breakStartTime = "";
      let breakEndTime = "";

      if (currentTimesheet.is_declined_timesheet && currentTimesheet.declined_timesheet) {
        // For declined timesheet, use the previously submitted values from declined_timesheet object
        jobStartTime = currentTimesheet.declined_timesheet.start_time ? currentTimesheet.declined_timesheet.start_time.slice(0, 5) : "";
        jobEndTime = currentTimesheet.declined_timesheet.end_time ? currentTimesheet.declined_timesheet.end_time.slice(0, 5) : "";
        breakStartTime = currentTimesheet.declined_timesheet.break_start_time ? currentTimesheet.declined_timesheet.break_start_time.slice(0, 5) : "";
        breakEndTime = currentTimesheet.declined_timesheet.break_end_time ? currentTimesheet.declined_timesheet.break_end_time.slice(0, 5) : "";
      } else {
        // For new timesheet, use the scheduled times from vacancy timeframe
        jobStartTime = currentTimesheet.start_time ? currentTimesheet.start_time.slice(0, 5) : "";
        jobEndTime = currentTimesheet.end_time ? currentTimesheet.end_time.slice(0, 5) : "";
        // break times remain empty for new timesheets
      }

      // Set form values
      setFieldValue("job_start_time", jobStartTime);
      setFieldValue("job_end_time", jobEndTime);
      setFieldValue("break_start_time", breakStartTime);
      setFieldValue("break_end_time", breakEndTime);
      setFieldValue("vacancy_id", currentTimesheet?.vacancy_uuid || "");
      setFieldValue("candidate_id", user?.uuid || "");
      setFieldValue("timeframe_id", currentTimesheet?.timeframe_id || 0);
    }
  }, [currentTimesheet, setFieldValue, user?.uuid]);

  // Update initial form values after form values are set
  useEffect(() => {
    if (currentTimesheet) {
      let jobStartTime = "";
      let jobEndTime = "";
      let breakStartTime = "";
      let breakEndTime = "";

      if (currentTimesheet.is_declined_timesheet && currentTimesheet.declined_timesheet) {
        jobStartTime = currentTimesheet.declined_timesheet.start_time ? currentTimesheet.declined_timesheet.start_time.slice(0, 5) : "";
        jobEndTime = currentTimesheet.declined_timesheet.end_time ? currentTimesheet.declined_timesheet.end_time.slice(0, 5) : "";
        breakStartTime = currentTimesheet.declined_timesheet.break_start_time ? currentTimesheet.declined_timesheet.break_start_time.slice(0, 5) : "";
        breakEndTime = currentTimesheet.declined_timesheet.break_end_time ? currentTimesheet.declined_timesheet.break_end_time.slice(0, 5) : "";
      } else {
        jobStartTime = currentTimesheet.start_time ? currentTimesheet.start_time.slice(0, 5) : "";
        jobEndTime = currentTimesheet.end_time ? currentTimesheet.end_time.slice(0, 5) : "";
      }

      // Update initial form values for comparison
      setInitialFormValues({
        job_start_time: jobStartTime,
        job_end_time: jobEndTime,
        break_start_time: breakStartTime,
        break_end_time: breakEndTime,
        candidate_id: user?.uuid || "",
        vacancy_id: currentTimesheet?.vacancy_uuid || "",
        timeframe_id: currentTimesheet?.timeframe_id || 0
      });
    }
  }, [currentTimesheet, user?.uuid]);

  const formatJobTimeframe = (
    date: string,
    startTime: string,
    endTime: string
  ): string => {
    const dateObj = new Date(`${date}T00:00:00`);

    // Format like: Thu 10 Jul 2025 (no comma after weekday)
    const dateParts = dateObj.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).split(', ');
    const dateStr = dateParts.join(' '); // Removes comma between weekday and date

    const formatTime = (time: string) => {
      const [h, m] = time.split(":");
      const d = new Date();
      d.setHours(Number(h), Number(m));
      const formatted = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      return formatted
        .replace(":00", "")
        .replace(/\s?(am|pm)/i, (_, meridiem) => meridiem.toLowerCase());
    };

    return `${dateStr}, ${formatTime(startTime)} to ${formatTime(endTime)}`;
  }

  const handleIDidNotWorkOnThisDayCheckBox = () => {
    if (!iDidNotWorkOnThisDay) {
      // Checking the box - save current values and clear fields
      setSavedTimeValues({
        job_start_time: values.job_start_time,
        job_end_time: values.job_end_time,
        break_start_time: values.break_start_time || "",
        break_end_time: values.break_end_time || "",
      });
      setFieldValue("job_start_time", "");
      setFieldValue("job_end_time", "");
      setFieldValue("break_start_time", "");
      setFieldValue("break_end_time", "");
      
      // Clear all validation errors
      setFieldError("job_start_time", "");
      setFieldError("job_end_time", "");
      setFieldError("break_start_time", "");
      setFieldError("break_end_time", "");
      
      setSelectedWorkType('not-worked');
    } else {
      // Unchecking the box - restore saved values
      setFieldValue("job_start_time", savedTimeValues.job_start_time);
      setFieldValue("job_end_time", savedTimeValues.job_end_time);
      setFieldValue("break_start_time", savedTimeValues.break_start_time);
      setFieldValue("break_end_time", savedTimeValues.break_end_time);
      setSelectedWorkType('worked');
    }
    setIDidNotWorkOnThisDay(!iDidNotWorkOnThisDay);
  }

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={"Submitted"}
          onClose={() => setShowNotification(false)}
        />
      )}
      {!isNotificationActive && (
      <PopupModal
        visible={showLogModal}
        showCloseBtn={false}
        customWidth={650}
        popUpClass="mobile-model"
        onClickClose={() => {
          setShowLogModal(false);
        }}
      >
        <div className="d-flex flex-column gap-15">
          <div className="d-flex gap-20 align-items-center justify-content-between">
            <div className="fs-22 fw-400 text-black">Log Timesheet</div>
            <div className="fs-16 fw-400 text-grey-dark">
              {currentTimesheetIndex + 1} of {totalTimeSheetCount}
            </div>
          </div>
          <div className="bg-off-white h-1px"></div>
          <div className="d-flex flex-column gap-15 mobile-job-scroll">
            <div className="fs-16 fw-400 text-black">
              You have completed the job below. Please submit your timesheet to
              continue using the portal.
            </div>
            <div className="h-1px"></div>
            <div className="d-flex gap-20">
              <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
                {currentTimesheet &&
                currentTimesheet?.centre_photo?.length > 0 ? (
                  <img
                    src={currentTimesheet?.centre_photo || ""}
                    alt="Centre-Photo"
                    width={50}
                    height={50}
                    className="img-responsive rounded-circle object-fit-cover"
                  />
                ) : (
                  <img
                    src={DefaultImg}
                    width={50}
                    height={50}
                    alt="Centre-Default"
                  />
                )}
              </div>
              <div className="flex-grow-1 d-flex flex-column gap-20">
                <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
                  <div className="fs-16 fw-700 text-black">
                    {currentTimesheet?.business_name},{" "}
                    <span className="fw-400">{currentTimesheet?.centre_address}</span>
                  </div>
                  <div className="fs-13 fw-400 text-black">
                    {currentTimesheet?.position_name || ""}
                  </div>
                </div>
                <div className="d-flex flex-column gap-3 fs-13 text-black">
                  <div className="fw-700">Job Timeframe</div>
                  <div className="fw-400">
                    {currentTimesheet &&
                      formatJobTimeframe(
                        currentTimesheet.date,
                        currentTimesheet.start_time,
                        currentTimesheet.end_time
                      )}
                  </div>
                </div>
              </div>
            </div>
            { currentTimesheet?.is_declined_timesheet ? 
              <div className="d-flex flex-column gap-15">
                <div className="mt-2 d-flex">
                  <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-items-center fs-13 fw-400">
                    <Warning />
                    Your timesheet has been declined. Please review the comments
                    below shared by the centre and revise the details where
                    necessary before submitting.
                  </div>
                </div>
                <div className="d-flex gap-10 align-items-center">
                  <div className="fs-16 fw-700 text-black">Status:</div>
                  <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
                    Declined
                  </div>
                </div>
                <div className="d-flex flex-column gap-5">
                  <div className="fs-16 fw-700 text-black">Centre Comment:</div>
                  <div className="fs-16 fw-400 text-black">
                    {currentTimesheet?.declined_reason ||
                      "No comments provided."}
                  </div>
                </div>
              </div> : ""
            }
            <div className="h-1px"></div>
            <div className="fs-16 fw-700 text-black">
              Log the details for the above job:
            </div>
            <div className="h-1px"></div>
            
              <ToggleButton
                options={options}
                selectedValue={selectedWorkType}
                onChange={(value) => {
                  const newValue = String(value);
                  setSelectedWorkType(newValue);
                  
                  if (newValue === 'not-worked') {
                    // Save current values before clearing
                    setSavedTimeValues({
                      job_start_time: values.job_start_time,
                      job_end_time: values.job_end_time,
                      break_start_time: values.break_start_time || "",
                      break_end_time: values.break_end_time || "",
                    });
                    
                    // Clear all time fields
                    setFieldValue("job_start_time", "");
                    setFieldValue("job_end_time", "");
                    setFieldValue("break_start_time", "");
                    setFieldValue("break_end_time", "");
                    
                    // Clear all validation errors
                    setFieldError("job_start_time", "");
                    setFieldError("job_end_time", "");
                    setFieldError("break_start_time", "");
                    setFieldError("break_end_time", "");
                    
                    setIDidNotWorkOnThisDay(true);
                  } else {
                    // Restore saved values when switching back to 'worked'
                    setFieldValue("job_start_time", savedTimeValues.job_start_time);
                    setFieldValue("job_end_time", savedTimeValues.job_end_time);
                    setFieldValue("break_start_time", savedTimeValues.break_start_time);
                    setFieldValue("break_end_time", savedTimeValues.break_end_time);
                    
                    setIDidNotWorkOnThisDay(false);
                  }
                }}
              />
            {selectedWorkType === 'worked' && (
              <>
                <div className="d-flex flex-column">
                  <div className="d-flex gap-15 gap-mobile-8 align-items-center">
                    <LogIn />
                    <div className="fs-16 fw-400 text-black min-w-140px mobile-min-w-65px">
                      Job start <span className="d-none d-lg-inline">time</span>
                    </div>
                    <div className="w-120px mobile-w-95px">
                      <TimePicker12Hr
                        onChange={(value) =>
                          handleTimeChange(value as string, "job_start_time")
                        }
                        value={values?.job_start_time}
                        className="form-select appearance-none"
                      />
                    </div>
                  </div>
                  {submitCount > 0 && errors?.job_start_time && (
                    <ErrorMessage message={errors?.job_start_time as string} />
                  )}
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex gap-15 gap-mobile-8 align-items-center">
                    <Break />
                    <div className="d-flex flex-column min-w-140px mobile-min-w-65px">
                      <div className="fs-16 fw-400 text-black">
                        Break <span className="d-none d-lg-inline">timeframe</span>
                      </div>
                      <div className="fs-13 fw-400 text-grey-medium">
                        (Optional)
                      </div>
                    </div>
                    <div className="w-120px mobile-w-95px">
                      <TimePicker12Hr
                        onChange={(value) =>
                          handleTimeChange(value as string, "break_start_time")
                        }
                        value={values?.break_start_time || ""}
                        className="form-select appearance-none"
                      />
                    </div>
                    <div className="fs-16 fw-400 text-black">–</div>
                    <div className="w-120px mobile-w-95px">
                      <TimePicker12Hr
                        onChange={(value) =>
                          handleTimeChange(value as string, "break_end_time")
                        }
                        value={values?.break_end_time || ""}
                        className="form-select appearance-none"
                      />
                    </div>
                  </div>
                  {submitCount > 0 && errors?.break_start_time && (
                    <ErrorMessage message={errors?.break_start_time as string} />
                  )}
                  {submitCount > 0 && errors?.break_end_time && (
                    <ErrorMessage message={errors?.break_end_time as string} />
                  )}
                </div>

                <div className="d-flex flex-column">
                  <div className="d-flex gap-15 gap-mobile-8 align-items-center">
                    <LogOut color="#167E76" />
                    <div className="fs-16 fw-400 text-black min-w-140px mobile-min-w-65px">
                      Job end <span className="d-none d-lg-inline">time</span>
                    </div>
                    <div className="w-120px mobile-w-95px">
                      <TimePicker12Hr
                        onChange={(value) =>
                          handleTimeChange(value as string, "job_end_time")
                        }
                        value={values?.job_end_time}
                        className="form-select appearance-none"
                      />
                    </div>
                  </div>
                  {submitCount > 0 && errors?.job_end_time && (
                    <ErrorMessage message={errors?.job_end_time as string} />
                  )}
                </div>
              </>
            )}
            <div className="d-flex justify-content-end align-items-center mt-10">
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black mobile-w-100"
                onClick={() => handleSubmit()}
                isLoading={inProgress}
                disabled={!hasValuesChanged() || inProgress}
              >
                <RoundedTick color={!hasValuesChanged() || inProgress ? "#aaaaaa" : "black"}/>
                Submit
              </CustomButton>
            </div>
          </div>
        </div>
      </PopupModal>
      )}
    </>
  );
};

export default Logtimesheet;